import { connect as mqttConnect, IClientOptions, MqttClient } from 'mqtt'
import { RootState } from '../store/reducers/persistedReducer';
import { doUpdateCells } from '../store/actions/cells';
import { doUpdateLayoutAndCells, doSetLayoutUpdateAvailability } from '../store/actions/channelLayout';
import Log from '../utils/Log';
import { selectCurrentChannelSettings } from '../store/selectors';
import { doSetMessageClientStatus } from '../store/actions/system';
import { ClusterApi } from "@cluster/web-components";
import { doAddNewAlert } from '../store/actions/channels';

if (typeof process.env.REACT_APP_CLUSTER_MESSAGE_HOST == "undefined" ||
    typeof process.env.REACT_APP_CLUSTER_MESSAGE_PORT == "undefined") {
    throw Error("API variables not found.");
}

class ClusterMessages {
  store: any
  host: string
  port: number
  client?: MqttClient
  connected: boolean

  constructor(store: any) {
    this.store = store;
    this.host = process.env.REACT_APP_CLUSTER_MESSAGE_HOST!;
    this.port = parseInt(process.env.REACT_APP_CLUSTER_MESSAGE_PORT!);
    this.connected = false;
  }

  getMessagingUrl(url: string, options: IClientOptions, client: MqttClient) {
    const state = this.store.getState() as RootState;

    client.options.username = state.user.msgCredentials?.username;
    client.options.password = state.user.msgCredentials?.password;

    const authUrl = url + "?token=" + state.user.token;

    return authUrl;
  }

  connect(callback?: (client: ClusterMessages) => void) {

    this.client = mqttConnect(("wss://" + this.host + ":" + this.port), {
      reconnectPeriod: 3000,
      transformWsUrl: this.getMessagingUrl.bind(this)
    });

    this.client.on('connect', () => {
      Log.info("Client Connected", "messages");
      this.connected = true;
      this.store.dispatch(doSetMessageClientStatus("Active"));

      if (callback) {
        callback(this);
      }
    });

    this.client.on('close', () => {
      Log.info("Client Closed", "messages");
      this.connected = false;
      this.store.dispatch(doSetMessageClientStatus("Inactive"));
    });

    this.client.on('reconnect', () => {
      Log.info("Client Closed", "messages");
      this.store.dispatch(doSetMessageClientStatus("Reconnecting"));
    });

    this.client.on('error', (err) => {
      Log.error(err.message, "messages");
      this.client?.end();
    });

    this.client.on("message", (topic, payload) => {
      this._receiveMessage(topic, payload);
    })
  }

  disconnect() {
    this.client?.end();
  }

  // Subscribe
  subscribe(type: "notifications" | "alerts", channelIds: number | number[]) {
    let ids = Array.isArray(channelIds) ? channelIds : [channelIds];
    let topics: string[] = [];

    if (type === "notifications") {
      topics = ids.map(id => "board/" + id + "/+");
    } else if (type === "alerts") {
      topics = ids.map(id => "board/" + id + "/alert");
    }

    Log.info("Subscribed to " + topics.join(", "), "messages");

    this.client?.subscribe(topics);
  }

  // Unsubscribe
  unsubscribe(type: "notifications" | "alerts", channelIds: number | number[]) {
    let ids = Array.isArray(channelIds) ? channelIds : [channelIds];
    let topics: string[] = [];

    if (type === "notifications") {
      topics = ids.map(id => "board/" + id + "/+");
    } else if (type === "alerts") {
      topics = ids.map(id => "board/" + id + "/alert");
    }

    Log.info("Unsubscribed to " + topics.join(", "), "messages");

    this.client?.unsubscribe(topics);
  }

  async _receiveMessage(topic: string, payload: Buffer) {
    const messageTopicComponents = topic.split("/");
    let jsonPayload;
    try {
      jsonPayload = JSON.parse(payload.toString());
    } catch(e) {
      jsonPayload = {};
    }

    if (messageTopicComponents[0] === "board" && messageTopicComponents[2] === "cells") {
      try {
        this.store.dispatch(doUpdateCells(jsonPayload));
      } catch (e) {
        Log.error(e, "messages");
      }
    }
    else if (messageTopicComponents[0] === "board" && messageTopicComponents[2] === "layoutUpdate") {
      const state = this.store.getState() as RootState;

      if (state.channelLayout.data.boardId === parseInt(messageTopicComponents[1])) {
        const channelSettings = selectCurrentChannelSettings(state);

        if (channelSettings.channelUpdates === "immediate") {
          this.store.dispatch(doUpdateLayoutAndCells());
        }
        else if (channelSettings.channelUpdates === "prompt") {
          this.store.dispatch(doSetLayoutUpdateAvailability(true));
        }

      }
    }
    else if (messageTopicComponents[0] === "board" && messageTopicComponents[2] === "alert") {
      const alert = jsonPayload as ClusterApi.Alert & {id: number};

      this.store.dispatch(doAddNewAlert(alert));
    }
  }
}

export default ClusterMessages
